// Main modules
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { RouterModule, Routes } from '@angular/router';

// Additional modules
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// https://github.com/ng-select/ng-select
import { NgSelectModule } from '@ng-select/ng-select';
// https://ckeditor.com/docs/ckeditor5/latest/builds/guides/integration/frameworks/angular.html
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';

// Material Components
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { AngularEditorModule } from '@kolkov/angular-editor';

// Pipes
import { BytesConversionPipe } from './common/pipes/bytes-conversion.pipe';
import { CountValueTransportTerm } from './common/pipes/count-value-transport-term.pipe';
import { CurrencyCodePipe } from './common/pipes/currency-code.pipe';
import { DateBoundsPipe } from './common/pipes/date-bounds.pipe';
import { DecodeBase64Pipe } from './common/pipes/decode-base64.pipe';
import { FormatDateTimePipe } from './common/pipes/format-date-time.pipe';
import { GenericMatAutocompleteComponent } from './common/generic-mat-autocomplete/generic-mat-autocomplete.component';
import { PercentValuePipe } from './common/pipes/percent-price.pipe';
import { PaymentStatusLabelPipe } from './common/pipes/payment-status-label.pipe';
import { PaymentTermStatusLabelPipe } from './common/pipes/payment-term-status-label.pipe';
import { PriceCurrencyPipe } from './common/pipes/price-currency.pipe';
import { PricePipe } from './common/pipes/price.pipe';
import { StatusLabelPipe } from './common/pipes/status-label.pipe';
import { YesNoPipe } from './common/pipes/yes-no.pipe';
import { NumberToDecimalStrPipe } from './common/pipes/number-to-decimal-str.pipe';
import { TransportKindIconPipe } from './common/pipes/transport-kind-icon.pipe';

// App components
import { PendingChangesGuard } from './common/pending-changes.guard';
import { EditorComponent } from './common/editor/editor.component';
import { DynamicTableComponent } from './common/dynamic-table/dynamic-table.component';
import { DynamicTableV2Component } from './common/dynamic-table-v2/dynamic-table-v2.component';
import { DynamicTableV2ColumnOptionDialogComponent } from './common/dynamic-table-v2/dynamic-table-v2-column-option-dialog/dynamic-table-v2-column-option-dialog.component';
import { QuestionDialogComponent } from './common/question-dialog/question-dialog.component';
import { EventsTimelineComponent } from './common/events-timeline/events-timeline.component';
import { PriceWithCurrencyComponent } from './common/price-with-currency/price-with-currency.component';
import { PhpTasksComponent } from './common/php-tasks/php-tasks.component';
import { SelectMultiComponent } from './common/select-multi/select-multi.component';
import { LoaderComponent } from './common/loader/loader.component';
import { StripHtmlPipe } from './common/striphtml.pipe';
import { DefinitionsComponent } from './definitions/definitions.component';
import { DefinitionsTagsLeadsComponent } from './definitions/definitions-tags-leads/definitions-tags-leads.component';
import { InfoboxComponent } from './common/infobox/infobox.component';
import { SelectComponent } from './common/inputs/select/select.component';
import { SimpleComponentComponent } from './common/simple-component/simple-component.component';
import { PhoneNumberComponent } from './common/phone-number/phone-number.component';
import { CheckboxesComponent } from './common/checkboxes/checkboxes.component';
import { RemindPasswordComponent } from './auth/remind-password/remind-password.component';
import { HistoryComponent } from './common/history/history.component';
import { VehiclesComponent } from './vehicles/vehicles.component';
import { VehiclesListComponent } from './vehicles/vehicles-list/vehicles-list.component';
import { VehiclesDocumentsListComponent } from './vehicles/vehicles-documents-list/vehicles-documents-list.component';
import { VehiclesFiltersComponent } from './vehicles/vehicles-filters/vehicles-filters.component';
import { VehiclesDocumentsFiltersComponent } from './vehicles/vehicles-documents-filters/vehicles-documents-filters.component';
import { VehiclesHistoryComponent } from './vehicles/vehicles-history/vehicles-history.component';
import { DriversComponent } from './drivers/drivers.component';

import { DriversCalendarComponent } from './drivers/drivers-calendar/drivers-calendar.component';
import { DriversCalendarFiltersComponent } from './drivers/drivers-calendar/drivers-calendar-filters/drivers-calendar-filters.component';
import { DriverEventComponent } from './drivers/driver-event/driver-event.component';
import { DriversHistoryComponent } from './drivers/drivers-history/drivers-history.component';
import { DriversSalaryDaysComponent } from './drivers/drivers-salary-days/drivers-salary-days.component';
import { LeadsComponent } from './leads/leads.component';
import { LeadsListComponent } from './leads/leads-list/leads-list.component';
import { LeadsShowComponent } from './leads/leads-show/leads-show.component';
import { LeadsShowTasksComponent } from './leads/leads-show-tasks/leads-show-tasks.component';
import { LeadsShowHistoryComponent } from './leads/leads-show-history/leads-show-history.component';
import { LeadsShowTagsComponent } from './leads/leads-show-tags/leads-show-tags.component';
import { LeadsListFiltersComponent } from './leads/leads-list-filters/leads-list-filters.component';
import { LeadsShowDetailComponent } from './leads/leads-show-detail/leads-show-detail.component';
import { LeadsHistoryComponent } from './leads/leads-history/leads-history.component';
import { TourismCalendarComponent } from './tourism-calendar/tourism-calendar.component';
import { TourismCalendarEventViewComponent } from './tourism-calendar/tourism-calendar-event-view/tourism-calendar-event-view.component';
import { TourismEventsComponent } from './tourism-events/tourism-events.component';
import { TourismEventsListComponent } from './tourism-events/tourism-events-list/tourism-events-list.component';
import { TourismEventsFiltersComponent } from './tourism-events/tourism-events-filters/tourism-events-filters.component';
import { TourismEventsHistoryComponent } from './tourism-events/tourism-events-history/tourism-events-history.component';
import { TourismStopsComponent } from './tourism/tourism-stops/tourism-stops.component';
import { TourismMapComponent } from './tourism/tourism-map/tourism-map.component';
import { TourismMapTransportComponent } from './tourism/tourism-map/tourism-map-transport/tourism-map-transport.component';
import { TourismMapPeopleComponent } from './tourism/tourism-map/tourism-map-people/tourism-map-people.component';
import { TourismBriefingComponent } from './tourism/tourism-briefing/tourism-briefing.component';
import { TourismTimetableComponent } from './tourism/tourism-timetable/tourism-timetable.component';
import { TourismTimetableVehiclesComponent } from './tourism/tourism-timetable/tourism-timetable-vehicles/tourism-timetable-vehicles.component';
import { TourismSchedulesComponent } from './tourism/tourism-schedules/tourism-schedules.component';
import { TourismSchedulesFiltersComponent } from './tourism/tourism-schedules/tourism-schedules-filters/tourism-schedules-filters.component';
import { TourismTransportListComponent } from './tourism/tourism-transport-list/tourism-transport-list.component';
import { TourismTransportListConfirmationComponent } from './tourism/tourism-transport-list/tourism-transport-list-confirmation/tourism-transport-list-confirmation.component';
import { TourismBriefingFiltersComponent } from './tourism/tourism-briefing/tourism-briefing-filters/tourism-briefing-filters.component';
import { TourismEventsDocumentsComponent } from './tourism-events/tourism-events-documents/tourism-events-documents.component';
import { TouristGuidesComponent } from './tourist-guides/tourist-guides.component';
import { TouristGuidesListComponent } from './tourist-guides/tourist-guides-list/tourist-guides-list.component';
import { TouristGuidesCalendarComponent } from './tourist-guides/tourist-guides-calendar/tourist-guides-calendar.component';
import { TouristGuidesFiltersComponent } from './tourist-guides/tourist-guides-calendar/tourist-guides-calendar-filters/tourist-guides-calendar-filters.component';
import { TouristGuidesEventComponent } from './tourist-guides/tourist-guides-event/tourist-guides-event.component';
import { TouristGuidesHistoryComponent } from './tourist-guides/tourist-guides-history/tourist-guides-history.component';
import { TouristGuidesRewardComponent } from './tourist-guides/tourist-guides-reward/tourist-guides-reward.component';
import { TouristGuidesRewardFiltersComponent } from './tourist-guides/tourist-guides-reward/tourist-guides-reward-filters/tourist-guides-reward-filters.component';
import { TouristGuidesRatesComponent } from './tourist-guides/tourist-guides-rates/tourist-guides-rates.component';
import { TouristGuidesIntegrationComponent } from './tourist-guides/tourist-guides-integration/tourist-guides-integration.component';
import { TouristGuidesSalaryListComponent } from './tourist-guides/tourist-guides-salary/tourist-guides-salary-list/tourist-guides-salary-list.component';
import { DriverLedgerListComponent } from './drivers/driver-ledger/driver-ledger-list/driver-ledger-list.component';
import { DriversIntegrationComponent } from './drivers/drivers-integration/drivers-integration.component';
import { DriversSalaryV2GratyfikantComponent } from './drivers/drivers-salary-v2/drivers-salary-v2-gratyfikant.component';
import { DriversSalaryV2Component } from './drivers/drivers-salary-v2/drivers-salary-v2.component';
import { DriversLedgerComponent } from './drivers/drivers-ledger/drivers-ledger.component';
import { CompanyComponent } from './company/company.component';
import { CompanyListComponent } from './company/company-list/company-list.component';
import { PhpTasksListComponent } from './php-tasks/php-tasks-list/php-tasks-list.component';
import { PhpTasksListFiltersComponent } from './php-tasks/php-tasks-list/php-tasks-list-filters/php-tasks-list-filters.component';
import { PhpTaskComponent } from './php-tasks/php-task/php-task.component';
import { OffersScanManageComponent } from './offers/offers-scan-manage/offers-scan-manage.component';
import { OffersListComponent } from './offers/offers-list/offers-list.component';
import { OffersShowComponent } from './offers/offers-show/offers-show.component';

import { OffersParmsComponent } from './offers/offers-parms/offers-parms.component';
import { SeasonComponent } from './season/season.component';
import { SeasonFiltersComponent } from './season/season-filters/season-filters.component';
import { ReservationListRefreshComponent } from './reservation-refresh/reservation-list/reservation-list.component';
import { ReservationRefreshComponent } from './reservation-refresh/reservation-refresh/reservation-refresh.component';
// import { PostComponent } from './post/post.component';
// import { PostListComponent } from './post/post-list/post-list.component';
import { UserComponent } from './users/user/user.component';
import { UsersListComponent } from './users/users-list/users-list.component';
import { UsersGroupsListComponent } from './users/users-groups-list/users-groups-list.component';
import { UsersGroupComponent } from './users/users-group/users-group.component';
import { UsersDepartmentsComponent } from './users/users-departments/users-departments.component';
import { SettingsComponent } from './users/settings/settings.component';
import { PostInComponent } from './post/post-in/post-in.component';
import { PostInListComponent } from './post/post-in-list/post-in-list.component';
import { PostOutComponent } from './post/post-out/post-out.component';
import { PostOutListComponent } from './post/post-out-list/post-out-list.component';
import { PostDocumentTypesComponent } from './post/post-document-types/post-document-types.component';
import { PostSendersComponent } from './post/post-senders/post-senders.component';
import { PostFiltersComponent } from './post/post-filters/post-filters.component';
import { TimetableListComponent } from './timetable/timetable-list/timetable-list.component';
import { TimetableComponent } from './timetable/timetable/timetable.component';
import { TimetableHoursComponent } from './timetable/timetable-hours/timetable-hours.component';
import { TimetableMapComponent } from './timetable/timetable-map/timetable-map.component';
import { TimetableTransportListsComponent } from './timetable/timetable-transport-lists/timetable-transport-lists.component';
import { TimetableListFiltersComponent } from './timetable/timetable-list/timetable-list-filters/timetable-list-filters.component';
import { BankAccountsComponent } from './bank/bank-accounts/bank-accounts.component';
import { BankAccountComponent } from './bank/bank-account/bank-account.component';
import { BankCardsComponent } from './bank/bank-cards/bank-cards.component';
import { BankCardComponent } from './bank/bank-card/bank-card.component';
import { BankAccountOwnersComponent } from './bank/bank-account-owners/bank-account-owners.component';
import { BankCardOwnersComponent } from './bank/bank-card-owners/bank-card-owners.component';
import { BankTransactionsTypesComponent } from './bank/bank-transactions-types/bank-transactions-types.component';
import { BankCardTransactionsComponent } from './bank/bank-card-transactions/bank-card-transactions.component';
import { BankTransactionsBulkUpdateDialogComponent } from './bank/bank-transactions/bank-transactions-bulk/bank-transactions-bulk-update-dialog.component';
import { BankTransactionsComponent } from './bank/bank-transactions/bank-transactions.component';
import { BankTransactionSplitComponent } from './bank/bank-transaction-split/bank-transaction-split.component';
import { BankTransactionsFiltersComponent } from './bank/bank-transactions/bank-transactions-filters/bank-transactions-filters.component';
import { BankCardSetComponent } from './bank/bank-card-set/bank-card-set.component';
import { BankTransactionsNamesComponent } from './bank/bank-transactions-names/bank-transactions-names.component';
import { TourismEventsHistoryDriversComponent } from './tourism-events/tourism-events-history-drivers/tourism-events-history-drivers.component';
import { PhpTaskAddComponent } from './common/php-tasks/php-task-add/php-task-add.component';
import { TourismEventsStopComponent } from './tourism-events/tourism-events-stop/tourism-events-stop.component';
import { TourismEventsFullComponent } from './tourism-events/tourism-events-full/tourism-events-full.component';
import { TourismEventsRentComponent } from './tourism-events/tourism-events-rent/tourism-events-rent.component';
import { TourismEventsServiceComponent } from './tourism-events/tourism-events-service/tourism-events-service.component';
import { EventBusinessPaymentsComponent } from './event-business/event-business-payments/event-business-payments.component';
import { EventBusinessIssuesComponent } from './event-business/event-business-issues/event-business-issues.component';
import { EventBusinessFiltersComponent } from './event-business/event-business-filters/event-business-filters.component';
import { EventBusinessIssuesTableComponent } from './event-business/event-business-issues-table/event-business-issues-table.component';
import { EventBusinessDocumentsComponent } from './event-business/event-business-documents/event-business-documents.component';
import { TransportLeadsComponent } from './leads/transport-leads/transport-leads.component';
import { TransportLeadsListComponent } from './leads/transport-leads-list/transport-leads-list.component';
import { TransportLeadsListFiltersComponent } from './leads/transport-leads-list/transport-leads-list-filters/transport-leads-list-filters.component';
import { TagsComponent } from './common/tags/tags.component';
import { TourismEventsDutyComponent } from './tourism-events/tourism-events-duty/tourism-events-duty.component';
import { TourismEventsLinkComponent } from './tourism-events/tourism-events-link/tourism-events-link.component';
import { BankHistoryOperationsComponent } from './bank/bank-history-operations/bank-history-operations.component';
import { DispatcherComponent } from './dispatcher/dispatcher.component';
import { TransportTemplatesListComponent } from './transport-templates/transport-templates-list/transport-templates-list.component';
import { TransportTemplatesMapComponent } from './transport-templates/transport-templates-map/transport-templates-map.component';
import { TransportsMapComponent } from './transports/transports-map/transports-map.component';
import { TransportsListComponent } from './transports/transports-list/transports-list.component';
import { FocustipComponent } from './common/focustip/focustip.component';
import { TransportTemplatesTableComponent } from './transport-templates/transport-templates-table/transport-templates-table.component';
import { TourismEventsFullDriversAddComponent } from './tourism-events/tourism-events-full/tourism-events-full-drivers-add/tourism-events-full-drivers-add.component';
import { TransportsTableComponent } from './transports/transports-table/transports-table.component';
import { DriversSalaryPaymentsComponent } from './drivers/drivers-salary-common/drivers-salary-summary-month/drivers-salary-payments/drivers-salary-payments.component';
import { DriversEventAddComponent } from './drivers/drivers-event-add/drivers-event-add.component';
import { TourismCalendarEventFiltersComponent } from './tourism-calendar/tourism-calendar-event-filters/tourism-calendar-event-filters.component';
import { AccommodationListComponent } from './accommodation/accommodation-list/accommodation-list.component';
import { AccommodationAvailabilitySetComponent } from './accommodation/accommodation-availability-set/accommodation-availability-set.component';
import { TransportsMapTemplatesComponent } from './transports/transports-map/transports-map-templates/transports-map-templates.component';
import { MapComponent } from './common/map/map.component';
import { MapCityComponent } from './common/map/map-city/map-city.component';
import { MapVehiclesComponent } from './common/map/map-vehicles/map-vehicles.component';
import { MapStopsComponent } from './common/map/map-stops/map-stops.component';
import { MapTimetableComponent } from './common/map/map-timetable/map-timetable.component';
import { TransportsCopyComponent } from './transports/transports-copy/transports-copy.component';
import { ResidentBaseTripListComponent } from './resident/resident-base-trip/resident-base-trip-list/resident-base-trip-list.component';
import { ResidentBaseTripComponent } from './resident/resident-base-trip/resident-base-trip.component';
import { ResidentTripComponent } from './resident/resident-trip/resident-trip.component';
import { ResidentTripListComponent } from './resident/resident-trip/resident-trip-list/resident-trip-list.component';
import { ResidentTripReservationComponent } from './resident/resident-trip/resident-trip-reservation/resident-trip-reservation.component';
import { ResidentTripReservationListComponent } from './resident/resident-trip/resident-trip-reservation-list/resident-trip-reservation-list.component';
import { ReservationsPeopleListComponent } from './resident/reservations-people-list/reservations-people-list.component';
import { ResidentDocumentComponent } from './resident/resident-document/resident-document.component';
import { ResidentCustomerComponent } from './resident/resident-customer/resident-customer.component';
import { ResidentCustomerListComponent } from './resident/resident-customer/resident-customer-list/resident-customer-list.component';
import { GoogleAuthComponent } from './auth/google-auth/google-auth.component';
import { AccommodationListProductsComponent } from './accommodation/accommodation-list/accommodation-list-products/accommodation-list-products.component';
import { AccommodationListProductComponent } from './accommodation/accommodation-list/accommodation-list-product/accommodation-list-product.component';
import { AccommodationListProductRoomComponent } from './accommodation/accommodation-list/accommodation-list-product/accommodation-list-product-room/accommodation-list-product-room.component';
import { AccommodationListReservationsComponent } from './accommodation/accommodation-list/accommodation-list-reservations/accommodation-list-reservations.component';
import { AccommodationListAvailabilitiesComponent } from './accommodation/accommodation-list/accommodation-list-availabilities/accommodation-list-availabilities.component';
import { AccommodationListReservationComponent } from './accommodation/accommodation-list/accommodation-list-reservation/accommodation-list-reservation.component';
import { AccommodationListMonthsComponent } from './accommodation/accommodation-list/accommodation-list-months/accommodation-list-months.component';
import { PromotionsListFiltersComponent } from './promotions/promotions-list/promotions-list-filters/promotions-list-filters.component';
import { PromotionsListComponent } from './promotions/promotions-list/promotions-list.component';
import { PromotionsComponent } from './promotions/promotions/promotions.component';
import { ServicesListComponent } from './services/services-list/services-list.component';
import { ServicesFormComponent } from './services/services-form/services-form.component';
import { NavigatorImportAnalizComponent } from './navigator/navigator-import-analiz/navigator-import-analiz.component';
import { SettlementBonusListComponent } from './settlement/settlement-bonus-list/settlement-bonus-list.component';
import { SettlementBonusComponent } from './settlement/settlement-bonus/settlement-bonus.component';
import { AccommodationGenerateComponent } from './accommodation/accommodation-generate/accommodation-generate.component';
import { ResidentSoldReservationComponent } from './resident/resident-sold-reservation/resident-sold-reservation.component';
import { ResidentSoldReservationFiltersComponent } from './resident/resident-sold-reservation/resident-sold-reservation-filters/resident-sold-reservation-filters.component';
import { ResidentTripListFilterComponent } from './resident/resident-trip/resident-trip-list/resident-trip-list-filter/resident-trip-list-filter.component';
import { ResidentCustomerListFilterComponent } from './resident/resident-customer/resident-customer-list/resident-customer-list-filter/resident-customer-list-filter.component';
import { ResidentBaseTripListFilterComponent } from './resident/resident-base-trip/resident-base-trip-list/resident-base-trip-list-filter/resident-base-trip-list-filter.component';
import { InsuranceGenerateComponent } from './tourism/insurance/insurance-generate/insurance-generate.component';
import { TourismEventsSeasonComponent } from './tourism-events/tourism-events-season/tourism-events-season.component';
import { AccommodationAvailabilityIstourComponent } from './accommodation/accommodation-availability-istour/accommodation-availability-istour.component';
import { ResidentSelectRestaurantComponent } from './resident/resident-customer/resident-customer-list/resident-select-restaurant/resident-select-restaurant.component';
import { TestingComponent } from './testing/testing.component';
import { GoogleContactsListComponent } from './users/google-contacts/google-contacts-list/google-contacts-list.component';
import { GoogleContactsComponent } from './users/google-contacts/google-contacts/google-contacts.component';
import { WwwMenuComponent } from './www/www-menu/www-menu.component';
import { WwwMenuListComponent } from './www/www-menu/www-menu-list/www-menu-list.component';
import { DriversSelectMonthComponent } from './drivers/drivers-salary-common/drivers-select-month/drivers-select-month.component';
import { DriversModalInfoComponent } from './drivers/drivers-salary-common/drivers-salary-summary-month/drivers-modal-info/drivers-modal-info.component';
import { DriversSalaryDaysListComponent } from './drivers/drivers-salary-common/drivers-salary-days-list/drivers-salary-days-list.component';
import { DriversSalarySummaryMonthComponent } from './drivers/drivers-salary-common/drivers-salary-summary-month/drivers-salary-summary-month.component';
import { DriversRateSetComponent } from './drivers/drivers-salary-common/drivers-rate-set/drivers-rate-set.component';
import { DriversDietSetComponent } from './drivers/drivers-salary-common/drivers-diet-set/drivers-diet-set.component';
import { TourismTransportFiltersComponent } from './tourism/tourism-transport-list/tourism-transport-filters/tourism-transport-filters.component';
import { DriversSalaryMonthListComponent } from './drivers/drivers-salary-common/drivers-salary-month-list/drivers-salary-month-list.component';
import { DriversSalaryMonthListFiltersComponent } from './drivers/drivers-salary-common/drivers-salary-month-list/drivers-salary-month-list-filters/drivers-salary-month-list-filters.component';
import { LocationsListComponent } from './locations/locations-list/locations-list.component';
import { LocationsListFiltersComponent } from './locations/locations-list/locations-list-filters/locations-list-filters.component';
import { LocationComponent } from './locations/location/location.component';
import { StopComponent } from './stops/stop/stop.component';

import { ProductsListComponent } from './products/products-list/products-list.component';
import { ProductComponent } from './products/product/product.component';
import { ProductsListFiltersComponent } from './products/products-list-filters/products-list-filters.component';
import { WwwBannerComponent } from './www/www-banner/www-banner.component';
import { WwwBannerListComponent } from './www/www-banner/www-banner-list/www-banner-list.component';
import { ProductBasicComponent } from './products/product/product-basic/product-basic.component';
import { ProductAdditionalComponent } from './products/product/product-additional/product-additional.component';
import { ProductMultimediasComponent } from './products/product/product-multimedias/product-multimedias.component';
import { ProductDescriptionComponent } from './products/product/product-description/product-description.component';
import { GroupOfferListComponent } from './group-offer/group-offer-list/group-offer-list.component';
import { GroupOfferListFiltersComponent } from './group-offer/group-offer-list-filters/group-offer-list-filters.component';
import { GroupOfferComponent } from './group-offer/group-offer/group-offer.component';
import { GroupOfferBasicComponent } from './group-offer/group-offer/group-offer-basic/group-offer-basic.component';
import { GroupOfferTermsComponent } from './group-offer/group-offer/group-offer-terms/group-offer-terms.component';
import { ProductRoomsComponent } from './products/product/product-rooms/product-rooms.component';
import { ProductRoomsAddComponent } from './products/product/product-rooms/product-rooms-add/product-rooms-add.component';
import { ProductRoomsEditComponent } from './products/product/product-rooms/product-rooms-edit/product-rooms-edit.component';
import { ProductRoomsSlaveComponent } from './products/product/product-rooms/product-rooms-slave/product-rooms-slave.component';
import { SettlementBonusListFilterComponent } from './settlement/settlement-bonus-list/settlement-bonus-list-filter/settlement-bonus-list-filter.component';
import { TermsComponent } from './terms/terms.component';
import { TermsFiltersComponent } from './terms/terms-filters/terms-filters.component';
import { CodebookListComponent } from './codebook/codebook-list/codebook-list.component';
import { CodebookComponent } from './codebook/codebook.component';
import { TagListComponent } from './codebook/tag-list/tag-list.component';
import { ConvenienceListComponent } from './codebook/convenience-list/convenience-list.component';
import { SellCategoryListComponent } from './codebook/sell-category-list/sell-category-list.component';
import { RoomTypeListComponent } from './codebook/room-type-list/room-type-list.component';
import { TagComponent } from './codebook/tag/tag.component';
import { ConvenienceComponent } from './codebook/convenience/convenience.component';
import { SellCategoryComponent } from './codebook/sell-category/sell-category.component';
import { RoomTypeComponent } from './codebook/room-type/room-type.component';
import { ContactsDestinyListComponent } from './codebook/contacts-destiny-list/contacts-destiny-list.component';
import { ContactsDestinyComponent } from './codebook/contacts-destiny/contacts-destiny.component';
import { AddressKindListComponent } from './codebook/address-kind-list/address-kind-list.component';
import { AddressKindComponent } from './codebook/address-kind/address-kind.component';
import { OfersCatalogListComponent } from './codebook/ofers-catalog-list/ofers-catalog-list.component';
import { OffersCatalogComponent } from './codebook/offers-catalog/offers-catalog.component';
import { DefaultCodebookListComponent } from './codebook/default-codebook-list/default-codebook-list.component';
import { DecriptionHeaderComponent } from './codebook/decription-header/decription-header.component';
import { DecriptionHeaderListComponent } from './codebook/decription-header-list/decription-header-list.component';
import { CurrencyComponent } from './codebook/currency/currency.component';
import { CurrencyListComponent } from './codebook/currency-list/currency-list.component';
import { DescripctionRequirementListComponent } from './codebook/descripction-requirement-list/descripction-requirement-list.component';
import { ProductKindListComponent } from './codebook/product-kind-list/product-kind-list.component';
import { DescriptionRequirementComponent } from './codebook/description-requirement/description-requirement.component';
import { GroupOfferPriceSettingComponent } from './group-offer/group-offer/group-offer-price-setting/group-offer-price-setting.component';
import { GroupOfferPriceComponent } from './group-offer/group-offer/group-offer-price/group-offer-price.component';

import { OptionalTripComponent } from './optional-trips/optional-trip/optional-trip.component';
import { OptionalTripListComponent } from './optional-trips/optional-trip-list/optional-trip-list.component';
import { OptionalTripMultimediasComponent } from './optional-trips/optional-trip/optional-trip-multimedias/optional-trip-multimedias.component';
import { OptionalTripBasicComponent } from './optional-trips/optional-trip/optional-trip-basic/optional-trip-basic.component';
import { OptionalTripLocationsComponent } from './optional-trips/optional-trip/optional-trip-locations/optional-trip-locations.component';
import { OptionalTripLocationAddComponent } from './optional-trips/optional-trip/optional-trip-locations/optional-trip-location-add/optional-trip-location-add.component';
import { LocationBasicComponent } from './locations/location/location-basic/location-basic.component';
import { LocationMultimediasComponent } from './locations/location/location-multimedias/location-multimedias.component';
import { LocationDescriptionsComponent } from './locations/location/location-descriptions/location-descriptions.component';
import { LocationDescriptionAddComponent } from './locations/location/location-descriptions/location-description-add/location-description-add.component';
import { LocationOptionalTripListComponent } from './locations/location/location-optional-trip-list/location-optional-trip-list.component';
import { EventBusinessPaymentsTableComponent } from './event-business/event-business-payments-table/event-business-payments-table.component';
import { EventBusinessPaymentsTableFiltersComponent } from './event-business/event-business-payments-table/event-business-payments-table-filters/event-business-payments-table-filters.component';
import { SettlementTermListComponent } from './settlement/settlement-term-list/settlement-term-list.component';
import { SettlementTermFiltersComponent } from './settlement/settlement-term-list/settlement-term-filters/settlement-term-filters.component';
import { GroupOfferPriceFiltersComponent } from './group-offer/group-offer/group-offer-price/group-offer-price-filters/group-offer-price-filters.component';
import { OrganizationListComponent } from './organization/organization-list/organization-list.component';
import { OrganizationFiltersComponent } from './organization/organization-filters/organization-filters.component';
import { OrganizationComponent } from './organization/organization/organization.component';
import { OrganizationPrimaryComponent } from './organization/organization/organization-primary/organization-primary.component';
import { OrganizationDataDownloadComponent } from './organization/organization/organization-data-download/organization-data-download.component';
import { OrganizationAddressAddComponent } from './organization/organization/organization-primary/organization-address-add/organization-address-add.component';
import { OrganizationBankAccountsComponent } from './organization/organization/organization-bank-accounts/organization-bank-accounts.component';
import { OrganizationWorkersAddComponent } from './organization/organization/organization-workers/organization-workers-add/organization-workers-add.component';
import { OrganizationWorkersComponent } from './organization/organization/organization-workers/organization-workers.component';
import { OrganizationAddressEditComponent } from './organization/organization/organization-primary/organization-address-edit/organization-address-edit.component';
import { OrganizationWorkersEditComponent } from './organization/organization/organization-workers/organization-workers-edit/organization-workers-edit.component';
import { SectionListComponent } from './sections/section-list/section-list.component';
import { SectionComponent } from './sections/section/section.component';
import { SectionFilterComponent } from './sections/section-filter/section-filter.component';
import { DriversDocumentsListComponent } from './drivers/drivers-documents-list/drivers-documents-list.component';
import { DriversDocumentsListFiltersComponent } from './drivers/drivers-documents-list/drivers-documents-list-filters/drivers-documents-list-filters.component';
import { GroupOfferPriceSettingDialogComponent } from './group-offer/group-offer/group-offer-price-setting-dialog/group-offer-price-setting-dialog.component';
import { GroupOfferPriceSettingPublicationDialogComponent } from './group-offer/group-offer/group-offer-price/group-offer-price-setting-publication-dialog/group-offer-price-setting-publication-dialog.component';
import { GroupOfferPriceHistoryDialogComponent } from './group-offer/group-offer/group-offer-price/group-offer-price-history-dialog/group-offer-price-history-dialog.component';
import { GroupOfferActivePriceSettingDialogComponent } from './group-offer/group-offer/group-offer-active-price-setting-dialog/group-offer-active-price-setting-dialog.component';
import { SeasonBagNewComponent } from './season-bag-new/season-bag-new.component';
import { SeasonBagNewFiltersComponent } from './season-bag-new/season-bag-new-filters/season-bag-new-filters.component';
import { SeasonBagNewModalComponent } from './season-bag-new/season-bag-new-modal/season-bag-new-modal.component';
import { SeasonBagNewModalStatusChangeComponent } from './season-bag-new/season-bag-new-modal-status-change/season-bag-new-modal-status-change.component';
import { TransportMapComponent } from '@/new-transport/transport-map/transport-map.component';
import { MapDialogComponent } from './common/map/map-dialog/map-dialog.component';
import { ServiceBasicComponent } from './services/services-form/service-basic/service-basic.component';
import { ServiceAdditionalComponent } from './services/services-form/service-additional/service-additional.component';
import { ServiceMultimediaComponent } from './services/services-form/service-multimedia/service-multimedia.component';
import { ServiceDialogComponent } from './services/services-form/service-dialog/service-dialog.component';
import { ReservationSummaryComponent } from './reservation/reservation-summary/reservation-summary.component';
import { ReservationSummaryOwnerComponent } from './reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner.component';
import { ReservationSummaryOwnerDialogComponent } from './reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner-dialog/reservation-summary-owner-dialog.component';
import { ReservationSummaryAgentComponent } from './reservation/reservation-summary/reservation-summary-agent/reservation-summary-agent.component';
import { ReservationSummaryAgentDialogComponent } from './reservation/reservation-summary/reservation-summary-agent/reservation-summary-agent-dialog/reservation-summary-agent-dialog.component';
import { ReservationSummaryAlertsComponent } from './reservation/reservation-summary/reservation-summary-alerts/reservation-summary-alerts.component';
import { ReservationSummaryStatusComponent } from './reservation/reservation-summary/reservation-summary-status/reservation-summary-status.component';
import { ReservationSummaryGuideComponent } from './reservation/reservation-summary/reservation-summary-guide/reservation-summary-guide.component';
import { ReservationSummaryCombineComponent } from './reservation/reservation-summary/reservation-summary-combine/reservation-summary-combine.component';
import { ReservationSummaryAccommodationComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-accommodation.component';
import { ReservationSummaryAccomodationOfferRoomsComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-accomodation-offer-rooms/reservation-summary-accomodation-offer-rooms.component';
import { ReservationSummaryAccomodationOfferRoomsDialogComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-accomodation-offer-rooms/reservation-summary-accomodation-offer-rooms-dialog/reservation-summary-accomodation-offer-rooms-dialog.component';
import { ReservationAccommodationOfferParticipantComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-accomodation-offer-participant/reservation-accommodation-offer-participant.component';
import { ReservationAccomodationOfferParticipantDialogComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-accomodation-offer-participant/reservation-accomodation-offer-participant-dialog/reservation-accomodation-offer-participant-dialog.component';
import { ReservationSummaryUserDocumentsComponent } from './reservation/reservation-summary/reservation-summary-user-documents/reservation-summary-user-documents.component';
import { ReservationAccomodationServiceComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-accommodation-service/reservation-accomodation-service.component';
import { ReservationSummaryAccomodationServiceDialogComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-accommodation-service/reservation-summary-accommodation-service-dialog/reservation-summary-accomodation-service-dialog.component';
import { ReservationSummaryPaymentsComponent } from './reservation/reservation-summary/reservation-summary-payments/reservation-summary-payments.component';
import { ReservationSummaryDocumentsComponent } from './reservation/reservation-summary/reservation-summary-documents/reservation-summary-documents.component';
import { ReservationSummaryDialogHandlerComponent } from './reservation/reservation-summary/reservation-summary-dialog-handler/reservation-summary-dialog-handler.component';
import { ReservationComponent } from './reservation/reservation.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion';
import { ReservationSummaryPaymentsDialogComponent } from './reservation/reservation-summary/reservation-summary-payments/reservation-summary-payments-dialog/reservation-summary-payments-dialog.component';
import { DriversSalaryConfirmHistoryComponent } from './drivers/drivers-salary-common/drivers-salary-confirm-history/drivers-salary-confirm-history.component';
import { SettlementBonusHistoryDetailComponent } from './settlement/settlement-bonus-history-detail/settlement-bonus-history-detail.component';
import { BagReservationListComponent } from './bag/bag-reservation-list/bag-reservation-list.component';
import { BagReservationListTableComponent } from './bag/bag-reservation-list/bag-reservation-list-table/bag-reservation-list-table.component';
import { BagReservationListFilterComponent } from './bag/bag-reservation-list/bag-reservation-list-filter/bag-reservation-list-filter.component';
import { BagReservationListStatisticComponent } from './bag/bag-reservation-list/bag-reservation-list-statistic/bag-reservation-list-statistic.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ReservationDocumentsComponent } from './reservation/reservation-documents/reservation-documents.component';
import { BankCardsFiltersComponent } from './bank/bank-cards/bank-cards-filters/bank-cards-filters.component';
import { AccommodationsComponent } from './accommodations/accommodations.component';
import { AccommodationsDatebarComponent } from './accommodations/accommodations-datebar/accommodations-datebar.component';
import { AccommodationsProductsSelectComponent } from './accommodations/accommodations-products-select/accommodations-products-select.component';
import { AccommodationsDestinationSelectComponent } from './accommodations/accommodations-destination-select/accommodations-destination-select.component';
import { AccommodationsProductsComponent } from './accommodations/accommodations-products/accommodations-products.component';
import { AccommodationsProductsRoomsComponent } from './accommodations/accommodations-products/accommodations-products-rooms/accommodations-products-rooms.component';
import { AccommodationsProductsRoomsAvailabilitiesComponent } from './accommodations/accommodations-products/accommodations-products-rooms/accommodations-products-rooms-availabilities/accommodations-products-rooms-availabilities.component';
import { AccommodationsAvailabilitySetComponent } from './accommodations/accommodations-availability-set/accommodations-availability-set.component';
import { BagTransportTermComponent } from './bag/bag-transport-term/bag-transport-term.component';
import { BagTransportFiltersComponent } from './bag/bag-transport-term/bag-transport-filters/bag-transport-filters.component';
import { BagTransportTermModalTransportComponent } from './bag/bag-transport-term/bag-transport-term-modal-transport/bag-transport-term-modal-transport.component';
import { TransportSegmentsComponent } from './bag/transport-segments/transport-segments.component';
import { TransportSegmentsAddComponent } from './bag/transport-segments/transport-segments-add/transport-segments-add.component';
import { BagPaymentTermListComponent } from './bag/bag-payment-term-list/bag-payment-term-list.component';
import { BagPaymentTermListTableComponent } from './bag/bag-payment-term-list/bag-payment-term-list-table/bag-payment-term-list-table.component';
import { BagPaymentTermListFiltersComponent } from './bag/bag-payment-term-list/bag-payment-term-list-table/bag-payment-term-list-filters/bag-payment-term-list-filters.component';
import { BagPaymentTermListTableSummaryComponent } from './bag/bag-payment-term-list/bag-payment-term-list-table/bag-payment-term-list-table-summary/bag-payment-term-list-table-summary.component';
import { BagPaymentListComponent } from './bag/bag-payment-list/bag-payment-list.component';
import { BagPaymentListTableComponent } from './bag/bag-payment-list/bag-payment-list-table/bag-payment-list-table.component';
import { BagPaymentListTableFiltersComponent } from './bag/bag-payment-list/bag-payment-list-table/bag-payment-list-table-filters/bag-payment-list-table-filters.component';
import { BagPaymentListTableSummaryComponent } from './bag/bag-payment-list/bag-payment-list-table/bag-payment-list-table-summary/bag-payment-list-table-summary.component';
import { BagPaymentListAddComponent } from './bag/bag-payment-list/bag-payment-list-add/bag-payment-list-add.component';
import { BagPaymentListAddModalComponent } from './bag/bag-payment-list/bag-payment-list-add/bag-payment-list-add-modal/bag-payment-list-add-modal.component';
import { BagPaymentListAddPositionModalComponent } from './bag/bag-payment-list/bag-payment-list-add/bag-payment-list-add-modal/bag-payment-list-add-position-modal/bag-payment-list-add-position-modal.component';
import { VehicleDeviceCmaComponent } from './vehicles/vehicle-device-cma/vehicle-device-cma.component';
import { WwwDescriptionTemplateComponent } from './www/www-description-template/www-description-template.component';
import { WwwDescriptionTemplateListComponent } from './www/www-description-template/www-description-template-list/www-description-template-list.component';
import { WwwMainDescriptionComponent } from './www/www-main-description/www-main-description.component';
import { WwwMainDescriptionListComponent } from './www/www-main-description/www-main-description-list/www-main-description-list.component';
import { NoteDialogComponent } from './note/note-dialog/note-dialog.component';
import { TasksDialogComponent } from './tasks/tasks-dialog/tasks-dialog.component';
import { ReservationTaskAndNotesComponent } from './reservation/reservation-task-and-notes/reservation-task-and-notes.component';
import { ConfirmTaskDialogComponent } from './tasks/tasks-list/confirm-task-dialog/confirm-task-dialog.component';
import { ReservationSummaryOfferComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-offer.component';
import { AccommodationsReservationComponent } from './accommodations/accommodations-reservation/accommodations-reservation.component';
import { ReservationSummaryCancelDialogComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-cancel-dialog/reservation-summary-cancel-dialog.component';
import { ReservationSummaryPaymentsSummaryComponent } from './reservation/reservation-summary/reservation-summary-payments/reservation-summary-payments-summary/reservation-summary-payments-summary.component';
import { ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-accomodation-offer-rooms/reservation-summary-accomodation-offer-rooms-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog.component';
import { AccommodationsProductWithoutRoomsComponent } from './accommodations/accommodations-product-without-rooms/accommodations-product-without-rooms.component';
import { BagPaymentListProfileAddComponent } from './bag/bag-payment-list/bag-payment-list-add/bag-payment-list-add-modal/bag-payment-list-profile-add/bag-payment-list-profile-add.component';
import { AccommodationsReservationsDokComponent } from './accommodations/accommodations-reservation/accommodations-reservations-dok/accommodations-reservations-dok.component';
import { OrganizationDialogAddComponent } from './organization/organization-dialog-add/organization-dialog-add.component';
import { ReservationSummaryGuideDialogComponent } from './reservation/reservation-summary/reservation-summary-guide/reservation-summary-guide-dialog/reservation-summary-guide-dialog.component';
import { ReservationSummaryAcommodationOfferParticipantChangeDialogComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-accomodation-offer-participant/reservation-summary-acommodation-offer-participant-change-dialog/reservation-summary-acommodation-offer-participant-change-dialog.component';
import { ReservationMessageListComponent } from './reservation/reservation-message-list/reservation-message-list.component';
import { ReservationMessageDialogComponent } from './reservation/reservation-message-list/reservation-message-dialog/reservation-message-dialog.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReservationMessageDetailsDialogComponent } from './reservation/reservation-message-list/reservation-message-dialog/reservation-message-details-dialog/reservation-message-details-dialog.component';
import { ReservationMessageSendComponent } from './reservation/reservation-message-list/reservation-message-send/reservation-message-send.component';
import { WwwFooterComponent } from './www/www-footer/www-footer.component';
import { DatePipe, NgOptimizedImage, registerLocaleData } from '@angular/common';
import { ReservationMessageGalleryComponent } from './reservation/reservation-message-list/reservation-message-dialog/reservation-message-gallery/reservation-message-gallery.component';
import { ReservationSummaryPaymentsUserdataDialogComponent } from './reservation/reservation-summary/reservation-summary-payments/reservation-summary-payments-userdata-dialog/reservation-summary-payments-userdata-dialog.component';
import { TourismEventsOrganizationComponent } from './tourism-events/tourism-events-organization/tourism-events-organization.component';
import { TourismEventsOrganizationDialogComponent } from './tourism-events/tourism-events-organization/tourism-events-organization-dialog/tourism-events-organization-dialog.component';
import { ServicesListFiltersComponent } from './services/services-list/services-list-filters/services-list-filters.component';
import { MessagesListComponent } from './message/messages-list/messages-list.component';
import { MessageComponent } from './message/message/message.component';
import { GeneralTasksListComponent } from './tasks/general-tasks-list/general-tasks-list.component';
import { ReservationSummaryInvoiceComponent } from './reservation/reservation-summary/reservation-summary-invoice/reservation-summary-invoice.component';
import { ReservationSummaryInvoiceDialogComponent } from './reservation/reservation-summary/reservation-summary-invoice/reservation-summary-invoice-dialog/reservation-summary-invoice-dialog.component';
import { MessageFiltersComponent } from './message/message-filters/message-filters.component';
import { DocumentDownloader } from './reservation/reservation-summary/reservation-summary-documents/document-downloader';
import { TourismEventsUserAddComponent } from './tourism-events/tourism-events-organization/tourism-events-user-add/tourism-events-user-add.component';
import { GeneralTasksFilterComponent } from './tasks/general-tasks-list/general-tasks-filter/general-tasks-filter.component';
import { ConfirmChangeDialogComponent } from './reservation/reservation-summary/reservation-summary-accomodation/reservation-summary-offer/reservation-summary-accomodation-offer-rooms/reservation-summary-accomodation-offer-rooms-dialog/reservation-summary-accomodation-offer-rooms-dialog-offer-change-dialog/confirm-change-dialog/confirm-change-dialog.component';
import { RequestTrackerInterceptor } from './common/interceptors/request-tracker.interceptor';
import { MessageTabsComponent } from './message/messages-list/message-tabs/message-tabs.component';
import { MessageTableComponent } from './message/messages-list/message-table/message-table.component';
import { MessageAssignDialogComponent } from './message/messages-list/message-assign-dialog/message-assign-dialog.component';
import { OfferRefreshButtonComponent } from './common/offer-refresh-button/offer-refresh-button.component';
import { SeasonListComponent } from './codebook/season-list/season-list.component';
import { CodebookSeasonComponent } from './codebook/season/season.component';
import { OrganizationBankAccountsAddEditComponent } from './organization/organization/organization-bank-accounts/organization-bank-accounts-add-edit/organization-bank-accounts-add-edit.component';
import { OrganizationContactAddEditDialogComponent } from './organization/organization/organization-primary/organization-contact-add-edit-dialog/organization-contact-add-edit-dialog.component';
import { TokenInterceptor } from './common/interceptors/token.interceptor';
import { AuthModule } from './auth/auth.module';
import { NavbarModule } from './common/navbar/navbar.module';
import { ReservationSearchModule } from './reservation-search/reservation-search.module';
import { AuthGuard } from './auth/auth.guard';
import { TourismEventsEmptyComponent } from './tourism-events/tourism-events-empty/tourism-events-empty.component';
import { FastFiltersComponent } from './common/fast-filters/fast-filters.component';
import { ReservationSummaryCommissionEditDialogComponent } from '@/reservation/reservation-summary/reservation-summary-commission-edit-dialog/reservation-summary-commission-edit-dialog.component';
import { StarsComponent } from '@/products/product/product-basic/stars/stars.component';
import localePl from '@angular/common/locales/pl';
import { TourismEventsFleetComponent } from './tourism-events/tourism-events-fleet/tourism-events-fleet.component';
import { environment } from 'src/environments/environment';
import { API_URL_TOKEN, ComfortPackageComponent } from '@oskar-frontend/comfort-package';
import { reservationResolver } from './reservation/reservation.resolver';
import { ComfortPackageDepartureComponent } from './reservation/comfort-package/comfort-package-departure/comfort-package-departure.component';
import { ComfortPackageReturnComponent } from './reservation/comfort-package/comfort-package-return/comfort-package-return.component';
import { ComfortPackageOneTransportComponent } from './reservation/comfort-package/comfort-package-one-transport/comfort-package-one-transport.component';
import { TourismEventsTechnicalEmptyComponent } from './tourism-events/tourism-events-technical-empty/tourism-events-technical-empty.component';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { TransportTimetableComponent } from '@/new-transport/transport-timetable/transport-timetable.component';
import { DisplayScanComponent } from './bank/bank-transactions/display-scan/display-scan.component';
import { TourismEventsVehicleAddComponent } from '@/tourism-events/tourism-events-full/tourism-events-vehicle-add/tourism-events-vehicle-add.component';
import { TourismEventsAntennaBusComponent } from '@/tourism-events/tourism-events-antenna-bus/tourism-events-antenna-bus.component';
import { TourismEventsMalfunctionComponent } from '@/tourism-events/tourism-events-malfunction/tourism-events-malfunction.component';
import { TourismEventsWashComponent } from '@/tourism-events/tourism-events-wash/tourism-events-wash.component';
import { TransportTemplateComponent } from '@/new-transport/transport-template/transport-template.component';
import { ConvertCommentPipe } from './common/pipes/convert-comment.pipe';
import { OwnerSearchComponent } from '@/reservation/reservation-summary/reservation-summary-owner/reservation-summary-owner-dialog/owner-search/owner-search.component';
import { OrganizationInsurerComponent } from '@/organization/organization/organization-insurer/organization-insurer.component';
import { ImportMassRebookingComponent } from './bank/import-mass-rebooking/import-mass-rebooking.component';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { TourismEventTransportTermSlotsComponent } from '@/tourism-calendar/tourism-calendar-event-view/tourism-event-transport-term-slots.component';
import { NoteListComponent } from '@/note/note-list/note-list.component';
import { TransportCoachListComponent } from './new-transport/transport-coach-list/transport-coach-list.component';
import { BankAccountFormatPipe } from './organization/organization/organization-bank-accounts/pipes/bank-account-format.pipe';
import { AppInitService } from './auth/app-init.service';
import { TransportListComponent } from './new-transport/transport-list/transport-list.component';
import { BagTransportTermTableComponent } from '@/bag/bag-transport-term/bag-transport-term-table/bag-transport-term-table.component';
import { httpErrorsInterceptor } from './common/interceptors/http-errors.interceptor';
import { TasksListModule } from './tasks/tasks-list/tasks-list.module';
import { ReservationSmsListComponent } from './reservation/reservation-sms-list/reservation-sms-list.component';
import { ProductOrganizationConfigComponent } from './products/product/product-organization-config/product-organization-config.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { ThemeChipSplitComponent } from './common/theme-chip-split/theme-chip-split.component';
import { ProductCheckListComponent } from './products/product/product-check-list/product-check-list.component';
import { BagHistoryComponent } from '@/common/bag-history/bag-history.component';
import { UtcDatetimeDirective } from './common/directives/utc-datetime.directive';
import { InvoiceComponent } from './vindication/invoice/invoice.component';
import { InvoiceMailComponent } from './vindication/invoice/invoice-mail/invoice-mail.component';
import { ContractorComponent } from './vindication/contractor/contractor.component';
import { ContractorEditComponent } from './vindication/contractor/contractor-edit/contractor-edit.component';
import { ProductCostsComponent } from './products/product/product-costs/product-costs.component';

registerLocaleData(localePl);

const routes: Routes = [
	{ path: '', redirectTo: '/login', pathMatch: 'full' },
	{
		path: 'testing',
		canActivate: [AuthGuard],
		component: TestingComponent,
	},
	{
		path: 'remind-password',
		canActivate: [AuthGuard],
		component: RemindPasswordComponent,
	},
	{
		path: 'remind-password/:code',
		canActivate: [AuthGuard],
		component: RemindPasswordComponent,
	},
	{
		path: 'settings',
		canActivate: [AuthGuard],
		component: SettingsComponent,
	},
	{
		path: 'googleAuth',
		canActivate: [AuthGuard],
		component: GoogleAuthComponent,
	},
	{
		path: 'dispatcher/:side',
		canActivate: [AuthGuard],
		component: DispatcherComponent,
	},
	{
		path: 'vehicles',
		canActivate: [AuthGuard],
		component: VehiclesListComponent,
	},
	{
		path: 'vehicles/documents',
		canActivate: [AuthGuard],
		component: VehiclesDocumentsListComponent,
	},
	{
		path: 'vehicles/:id',
		canActivate: [AuthGuard],
		component: VehiclesComponent,
	},
	{
		path: 'vehicles/:id/history',
		canActivate: [AuthGuard],
		component: VehiclesHistoryComponent,
	},
	{
		path: 'drivers',
		canActivate: [AuthGuard],
		loadComponent: () =>
			import('./drivers/drivers-list/drivers-list.component').then((c) => c.DriversListComponent),
	},
	{
		path: 'drivers/salary',
		canActivate: [AuthGuard],
		component: DriversSalaryMonthListComponent,
	},
	{
		path: 'drivers/ledger',
		canActivate: [AuthGuard],
		component: DriversLedgerComponent,
	},
	{
		path: 'drivers/:id',
		canActivate: [AuthGuard],
		component: DriversComponent,
	},
	{
		path: 'drivers/:id/history',
		canActivate: [AuthGuard],
		component: DriversHistoryComponent,
	},
	{
		path: 'drivers-calendar',
		canActivate: [AuthGuard],
		component: DriversCalendarComponent,
	},
	{
		path: 'drivers-calendar/:date',
		canActivate: [AuthGuard],
		component: DriversCalendarComponent,
	},
	{
		path: 'driver-event/add/:date',
		canActivate: [AuthGuard],
		component: DriverEventComponent,
	},
	{
		path: 'drivers-event/add',
		canActivate: [AuthGuard],
		component: DriverEventComponent,
	},
	{
		path: 'driver-event/:id',
		canActivate: [AuthGuard],
		component: DriverEventComponent,
	},
	{
		path: 'documentsList',
		canActivate: [AuthGuard],
		component: DriversDocumentsListComponent,
	},
	{
		path: 'transportTemplates',
		canActivate: [AuthGuard],
		component: TransportTemplatesListComponent,
	},
	{
		path: 'transportTemplates/:id',
		canActivate: [AuthGuard],
		component: TransportTemplatesMapComponent,
	},
	{
		path: 'transportTemplates/:id/table',
		canActivate: [AuthGuard],
		component: TransportTemplatesTableComponent,
	},
	{
		path: 'transport/transport/:id',
		canActivate: [AuthGuard],
		component: TransportMapComponent,
	},
	{
		path: 'transport/map/new',
		canActivate: [AuthGuard],
		component: TransportMapComponent,
	},
	{
		path: 'transport/:id/map/new',
		canActivate: [AuthGuard],
		component: TransportMapComponent,
	},
	{
		path: 'transport/map/new',
		canActivate: [AuthGuard],
		component: TransportMapComponent,
	},
	{
		path: 'transport/template',
		canActivate: [AuthGuard],
		component: TransportTemplateComponent,
	},
	{
		path: 'transport/:id/map/new',
		canActivate: [AuthGuard],
		component: TransportMapComponent,
	},
	{
		path: 'transport/:id/table/new',
		canActivate: [AuthGuard],
		component: TransportTimetableComponent,
	},
	{
		path: 'transport/:id/coach',
		canActivate: [AuthGuard],
		component: TransportCoachListComponent,
	},
	{
		path: 'transport/:id/list',
		canActivate: [AuthGuard],
		component: TransportListComponent,
	},
	{
		path: 'segment/:id/list',
		canActivate: [AuthGuard],
		component: TransportListComponent,
	},
	{
		path: 'accommodationsOld',
		canActivate: [AuthGuard],
		component: AccommodationListComponent,
	},
	{
		path: 'accommodation-file',
		canActivate: [AuthGuard],
		loadComponent: () =>
			import('./accommodation-file/accommodation-file.component').then((c) => c.AccommodationFileComponent),
	},
	{
		path: 'accommodations',
		canActivate: [() => false],
		component: AccommodationsComponent,
	},
	{
		path: 'accommodations/istour',
		canActivate: [AuthGuard],
		component: AccommodationAvailabilityIstourComponent,
	},
	{
		path: 'accommodations/istour/:dateFrom/:dateTo/:product/:roomType/:fullscreen',
		canActivate: [AuthGuard],
		component: AccommodationAvailabilityIstourComponent,
	},
	{
		path: 'accommodation-file/generate',
		canActivate: [AuthGuard],
		component: AccommodationGenerateComponent,
	},
	{
		path: 'transports',
		canActivate: [AuthGuard],
		component: TransportsListComponent,
	},
	{
		path: 'transports/:id',
		canActivate: [AuthGuard],
		component: TransportsMapComponent,
	},
	{
		path: 'transports/:id/table',
		canActivate: [AuthGuard],
		component: TransportsTableComponent,
	},
	{
		path: 'transports/:id/copy',
		canActivate: [AuthGuard],
		component: TransportsCopyComponent,
	},
	{
		path: 'seasonBagNew',
		canActivate: [AuthGuard],
		component: SeasonBagNewComponent,
	},
	{
		path: 'transportTerm',
		canActivate: [AuthGuard],
		component: BagTransportTermComponent,
	},
	{
		path: 'transport-segments/add',
		canActivate: [AuthGuard],
		component: TransportSegmentsAddComponent,
	},
	{
		path: 'transport-segments/:id',
		canActivate: [AuthGuard],
		component: TransportSegmentsAddComponent,
	},
	{
		path: 'transport-segments',
		canActivate: [AuthGuard],
		component: TransportSegmentsComponent,
	},
	{
		path: 'touristGuides',
		canActivate: [AuthGuard],
		component: TouristGuidesListComponent,
	},
	{
		path: 'touristGuides/:id',
		canActivate: [AuthGuard],
		component: TouristGuidesComponent,
	},
	{
		path: 'touristGuides/:id/history',
		canActivate: [AuthGuard],
		component: TouristGuidesHistoryComponent,
	},
	{
		path: 'tourist-guides-calendar',
		canActivate: [AuthGuard],
		component: TouristGuidesCalendarComponent,
	},
	{
		path: 'tourist-guides-calendar/:date',
		canActivate: [AuthGuard],
		component: TouristGuidesCalendarComponent,
	},
	{
		path: 'tourist-guides-event/:id',
		canActivate: [AuthGuard],
		component: TouristGuidesEventComponent,
	},
	{
		path: 'tourist-guides-reward',
		canActivate: [AuthGuard],
		component: TouristGuidesRewardComponent,
	},
	{
		path: 'tourist-guides-salary',
		canActivate: [AuthGuard],
		component: TouristGuidesSalaryListComponent,
	},
	{
		path: 'touristGuides/:id/salary/:salaryId',
		canActivate: [AuthGuard],
		loadComponent: () =>
			import(
				'./tourist-guides/tourist-guides-salary/tourist-guides-salary-form/tourist-guides-salary-form.component'
			).then((c) => c.TouristGuidesSalaryFormComponent),
	},
	{
		path: 'tourism-guides-file-list',
		canActivate: [AuthGuard],
		loadChildren: () =>
			import('./tourist-guides/tourism-guides-file/tourism-guides-file.routes').then(
				(m) => m.TOURISM_GUIDES_FILE_ROUTES,
			),
	},
	{
		path: 'flight-grid',
		canActivate: [AuthGuard],
		loadComponent: () => import('./flight-grid/flight-grid.component').then((c) => c.FlightGridComponent),
	},
	{
		path: 'leads',
		canActivate: [AuthGuard],
		component: LeadsListComponent,
	},
	{
		path: 'leads/group',
		canActivate: [AuthGuard],
		component: LeadsListComponent,
	},
	{
		path: 'leads/individual',
		canActivate: [AuthGuard],
		component: LeadsListComponent,
	},
	{
		path: 'leads/add',
		canActivate: [AuthGuard],
		component: LeadsComponent,
	},
	{
		path: 'leads/:id',
		canActivate: [AuthGuard],
		component: LeadsShowComponent,
	},
	{
		path: 'leads/:id/history',
		canActivate: [AuthGuard],
		component: LeadsHistoryComponent,
	},
	{
		path: 'leads/:id/edit',
		canActivate: [AuthGuard],
		component: LeadsComponent,
	},
	{
		path: 'transport-leads',
		canActivate: [AuthGuard],
		component: TransportLeadsListComponent,
	},
	{
		path: 'transport-leads/:id',
		canActivate: [AuthGuard],
		component: TransportLeadsComponent,
	},
	{
		path: 'definitions',
		canActivate: [AuthGuard],
		component: DefinitionsComponent,
	},
	{
		path: 'definitions/tags-leads',
		canActivate: [AuthGuard],
		component: DefinitionsTagsLeadsComponent,
	},
	{
		path: 'tourism-calendar',
		canActivate: [AuthGuard],
		component: TourismCalendarComponent,
	},
	{
		path: 'tourism-calendar/:date',
		canActivate: [AuthGuard],
		component: TourismCalendarComponent,
	},
	{
		path: 'tourism-events',
		canActivate: [AuthGuard],
		component: TourismEventsListComponent,
	},
	{
		path: 'tourism-events/add',
		canActivate: [AuthGuard],
		component: TourismEventsComponent,
	},
	{
		path: 'tourism-events/add/stop',
		canActivate: [AuthGuard],
		component: TourismEventsStopComponent,
	},
	{
		path: 'tourism-events/add/:category/rent',
		canActivate: [AuthGuard],
		component: TourismEventsRentComponent,
	},
	{
		path: 'tourism-events/add/empty',
		canActivate: [AuthGuard],
		component: TourismEventsEmptyComponent,
	},
	{
		path: 'tourism-events/add/service',
		canActivate: [AuthGuard],
		component: TourismEventsServiceComponent,
	},
	{
		path: 'tourism-events/add/duty',
		canActivate: [AuthGuard],
		component: TourismEventsDutyComponent,
	},
	{
		path: 'tourism-events/add/fleet',
		canActivate: [AuthGuard],
		component: TourismEventsFleetComponent,
	},
	{
		path: 'tourism-events/add/technical-empty',
		canActivate: [AuthGuard],
		component: TourismEventsTechnicalEmptyComponent,
	},
	{
		path: 'tourism-events/add/wash',
		canActivate: [AuthGuard],
		component: TourismEventsWashComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'tourism-events/add/accident',
		canActivate: [AuthGuard],
		component: TourismEventsMalfunctionComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'tourism-events/add/antenna-bus',
		canActivate: [AuthGuard],
		component: TourismEventsAntennaBusComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'tourism-events/add/full',
		canActivate: [AuthGuard],
		component: TourismEventsFullComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'tourism-events/:id',
		canActivate: [AuthGuard],
		component: TourismEventsFullComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'tourism-events/:id/history',
		canActivate: [AuthGuard],
		component: TourismEventsHistoryComponent,
	},
	{
		path: 'tourism-events/:id/history-drivers',
		canActivate: [AuthGuard],
		component: TourismEventsHistoryDriversComponent,
	},
	{
		path: 'tourism-events/:id/documents/:type',
		canActivate: [AuthGuard],
		component: TourismEventsDocumentsComponent,
	},
	{
		path: 'tourism-stops',
		canActivate: [AuthGuard],
		component: TourismStopsComponent,
	},
	{ path: 'tourism-map', redirectTo: '/season', pathMatch: 'full' },
	{
		path: 'tourism-map/:id',
		canActivate: [AuthGuard],
		component: TourismMapComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'tourism-insurance',
		canActivate: [AuthGuard],
		component: InsuranceGenerateComponent,
	},
	{
		path: 'tourism-map/:side/:elements',
		canActivate: [AuthGuard],
		component: TourismMapComponent,
	},
	{
		path: 'tourism-timetable/:id',
		canActivate: [AuthGuard],
		component: TourismTimetableComponent,
	},
	{
		path: 'tourism-schedules',
		canActivate: [AuthGuard],
		component: TourismSchedulesComponent,
	},
	{
		path: 'tourism-schedules/:id',
		canActivate: [AuthGuard],
		component: TourismSchedulesComponent,
	},
	{
		path: 'tourism-transport-list/:id',
		canActivate: [AuthGuard],
		component: TourismTransportListComponent,
	},
	{
		path: 'tourism-transport-list-confirmations/:id',
		canActivate: [AuthGuard],
		component: TourismTransportListConfirmationComponent,
	},
	{
		path: 'tourism-briefing/:side',
		canActivate: [AuthGuard],
		component: TourismBriefingComponent,
	},
	{
		path: 'timetable',
		canActivate: [AuthGuard],
		component: TimetableListComponent,
	},
	{
		path: 'timetable/:id',
		canActivate: [AuthGuard],
		component: TimetableComponent,
	},
	{
		path: 'timetable/:id/hours',
		canActivate: [AuthGuard],
		component: TimetableHoursComponent,
	},
	{
		path: 'timetable/:id/map',
		canActivate: [AuthGuard],
		component: TimetableMapComponent,
	},
	{
		path: 'timetable/:id/transport-lists',
		canActivate: [AuthGuard],
		component: TimetableTransportListsComponent,
	},
	{
		path: 'reservation',
		canActivate: [AuthGuard],
		component: ReservationComponent,
	},
	{
		path: 'reservationList',
		canActivate: [AuthGuard],
		component: BagReservationListComponent,
	},
	{
		path: 'reservation/:id',
		canActivate: [AuthGuard],
		resolve: { reservation: reservationResolver },
		component: ReservationComponent,
	},
	{
		path: 'reservations-refresh',
		canActivate: [AuthGuard],
		component: ReservationListRefreshComponent,
	},
	{
		path: 'reservations-refresh/refresh',
		canActivate: [AuthGuard],
		component: ReservationRefreshComponent,
	},
	{
		path: 'users',
		canActivate: [AuthGuard],
		component: UsersListComponent,
	},
	{
		path: 'users/:id',
		canActivate: [AuthGuard],
		component: UserComponent,
	},
	{
		path: 'usersDepartments',
		canActivate: [AuthGuard],
		component: UsersDepartmentsComponent,
	},
	{
		path: 'usersGroups',
		canActivate: [AuthGuard],
		component: UsersGroupsListComponent,
	},
	{
		path: 'usersGroups/:id',
		canActivate: [AuthGuard],
		component: UsersGroupComponent,
	},
	{
		path: 'googleContacts',
		canActivate: [AuthGuard],
		component: GoogleContactsListComponent,
	},
	{
		path: 'googleContacts/:id',
		canActivate: [AuthGuard],
		component: GoogleContactsComponent,
	},
	{
		path: 'company',
		canActivate: [AuthGuard],
		component: CompanyListComponent,
	},
	{
		path: 'company/add',
		canActivate: [AuthGuard],
		component: CompanyComponent,
	},
	{
		path: 'company/:id',
		canActivate: [AuthGuard],
		component: CompanyComponent,
	},
	{
		path: 'task',
		canActivate: [AuthGuard],
		component: PhpTasksListComponent,
	},
	{
		path: 'task/add',
		canActivate: [AuthGuard],
		component: PhpTaskComponent,
	},
	{
		path: 'task/:id',
		canActivate: [AuthGuard],
		component: PhpTaskComponent,
	},
	{
		path: 'offers',
		canActivate: [AuthGuard],
		component: OffersListComponent,
	},
	{
		path: 'offers/:id',
		canActivate: [AuthGuard],
		component: OffersShowComponent,
	},
	{
		path: 'offers/:id/parms',
		canActivate: [AuthGuard],
		component: OffersParmsComponent,
	},
	{
		path: 'offers/scanManage',
		canActivate: [AuthGuard],
		component: OffersScanManageComponent,
	},
	{
		path: 'season',
		canActivate: [AuthGuard],
		component: SeasonComponent,
	},
	{
		path: 'postIn',
		canActivate: [AuthGuard],
		component: PostInListComponent,
	},
	{
		path: 'postIn/add',
		canActivate: [AuthGuard],
		component: PostInComponent,
	},
	{
		path: 'postIn/:id',
		canActivate: [AuthGuard],
		component: PostInComponent,
	},
	{
		path: 'postOut',
		canActivate: [AuthGuard],
		component: PostOutListComponent,
	},
	{
		path: 'postOut/add',
		canActivate: [AuthGuard],
		component: PostOutComponent,
	},
	{
		path: 'postOut/:id',
		canActivate: [AuthGuard],
		component: PostOutComponent,
	},
	{
		path: 'alior-status',
		canActivate: [AuthGuard],
		loadComponent: () => import('./bank/alior-status/alior-status.component').then((m) => m.AliorStatusComponent),
	},
	{
		path: 'integrations',
		canActivate: [AuthGuard],
		loadChildren: () => import('./integrations/integrations.routes').then((m) => m.INTEGRATIONS_ROUTES),
	},
	{
		path: 'bankAccounts',
		canActivate: [AuthGuard],
		component: BankAccountsComponent,
	},
	{
		path: 'bankAccounts/:id',
		canActivate: [AuthGuard],
		component: BankAccountComponent,
	},
	{
		path: 'bankCards',
		canActivate: [AuthGuard],
		component: BankCardsComponent,
	},
	{
		path: 'bankCards/:id',
		canActivate: [AuthGuard],
		component: BankCardComponent,
	},
	{
		path: 'bankCards/:id/owners',
		canActivate: [AuthGuard],
		component: BankCardOwnersComponent,
	},
	{
		path: 'bankTransactions',
		canActivate: [AuthGuard],
		component: BankTransactionsComponent,
	},
	{
		path: 'bankTransactions/transfers',
		canActivate: [AuthGuard],
		component: BankTransactionsComponent,
	},
	{
		path: 'bankTransactions/scan/:id',
		canActivate: [AuthGuard],
		component: DisplayScanComponent,
	},
	{
		path: 'bankTransactions/:id/split',
		canActivate: [AuthGuard],
		component: BankTransactionSplitComponent,
	},
	{
		path: 'bankTransactions/transfers/:id/split',
		canActivate: [AuthGuard],
		component: BankTransactionSplitComponent,
	},
	{
		path: 'bankTransactions/:dateFrom/:dateTo',
		canActivate: [AuthGuard],
		component: BankTransactionsComponent,
	},
	{
		path: 'bankTransactionsTypes',
		canActivate: [AuthGuard],
		component: BankTransactionsTypesComponent,
	},
	{
		path: 'bankHistoryOperations',
		canActivate: [AuthGuard],
		component: BankHistoryOperationsComponent,
	},
	{
		path: 'bankTransactionsNames',
		canActivate: [AuthGuard],
		component: BankTransactionsNamesComponent,
	},
	{
		path: 'eventBusiness/:id/payments',
		canActivate: [AuthGuard],
		component: EventBusinessIssuesComponent,
	},
	{
		path: 'eventBusiness/table',
		canActivate: [AuthGuard],
		component: EventBusinessIssuesTableComponent,
	},
	{
		path: 'promotions',
		canActivate: [AuthGuard],
		component: PromotionsListComponent,
	},
	{
		path: 'vouchers',
		canActivate: [AuthGuard],
		loadComponent: () => import('./vouchers/vouchers.component').then((c) => c.VouchersComponent),
	},
	{
		path: 'add-voucher',
		canActivate: [AuthGuard],
		loadComponent: () => import('./vouchers/add-voucher/add-voucher.component').then((c) => c.AddVoucherComponent),
	},
	{
		path: 'edit-voucher/:id',
		canActivate: [AuthGuard],
		loadComponent: () => import('./vouchers/add-voucher/add-voucher.component').then((c) => c.AddVoucherComponent),
	},
	{
		path: 'promotions/:id',
		canActivate: [AuthGuard],
		component: PromotionsComponent,
	},
	{
		path: 'service',
		canActivate: [AuthGuard],
		component: ServicesListComponent,
	},
	{
		path: 'service/:id',
		canActivate: [AuthGuard],
		component: ServicesFormComponent,
	},
	{
		path: 'eventBusiness/:id/payments',
		canActivate: [AuthGuard],
		component: EventBusinessIssuesComponent,
	},
	{
		path: 'eventBusiness/:id/payments/documents',
		canActivate: [AuthGuard],
		component: EventBusinessDocumentsComponent,
	},
	{
		path: 'eventBusiness/table',
		canActivate: [AuthGuard],
		component: EventBusinessIssuesTableComponent,
	},
	{
		path: 'residentBaseTrips',
		canActivate: [AuthGuard],
		component: ResidentBaseTripListComponent,
	},
	{
		path: 'residentBaseTrips/:id',
		canActivate: [AuthGuard],
		component: ResidentBaseTripComponent,
	},
	{
		path: 'resident/residentTrips',
		canActivate: [AuthGuard],
		component: ResidentTripListComponent,
	},
	{
		path: 'resident/residentTrips/:id',
		canActivate: [AuthGuard],
		component: ResidentTripComponent,
	},
	{
		path: 'residentTrips/documents/:type/:id',
		canActivate: [AuthGuard],
		component: ResidentDocumentComponent,
	},
	{
		path: 'residentTripsReservation/:id',
		canActivate: [AuthGuard],
		component: ResidentTripReservationComponent,
	},
	{
		path: 'residentTripsReservation/',
		canActivate: [AuthGuard],
		component: ResidentTripReservationComponent,
	},
	{
		path: 'resident/reservationsPeopleList/:id',
		canActivate: [AuthGuard],
		component: ReservationsPeopleListComponent,
	},
	{
		path: 'import',
		canActivate: [AuthGuard],
		component: NavigatorImportAnalizComponent,
	},
	{
		path: 'settlements/settlementBonus',
		canActivate: [AuthGuard],
		component: SettlementBonusListComponent,
	},
	{
		path: 'settlements/settlementBonusHistoryDetail/:id',
		canActivate: [AuthGuard],
		component: SettlementBonusHistoryDetailComponent,
	},
	{
		path: 'settlements/settlementBonusTerm',
		canActivate: [AuthGuard],
		component: SettlementTermListComponent,
	},
	{
		path: 'settlements/settlementBonus/:id',
		canActivate: [AuthGuard],
		component: SettlementBonusComponent,
	},
	{
		path: 'residentTripReservationList',
		canActivate: [AuthGuard],
		component: ResidentTripReservationListComponent,
	},
	{
		path: 'residentCustomerList',
		canActivate: [AuthGuard],
		component: ResidentCustomerListComponent,
	},
	{
		path: 'residentSoldTrips',
		canActivate: [AuthGuard],
		component: ResidentSoldReservationComponent,
	},
	{
		path: 'eventBusiness/:id/payments',
		canActivate: [AuthGuard],
		component: EventBusinessIssuesComponent,
	},
	{
		path: 'eventBusiness/table',
		canActivate: [AuthGuard],
		component: EventBusinessIssuesTableComponent,
	},
	{
		path: 'www/menu',
		canActivate: [AuthGuard],
		component: WwwMenuListComponent,
	},
	{
		path: 'www/menu/:id',
		canActivate: [AuthGuard],
		component: WwwMenuComponent,
	},
	{
		path: 'location',
		canActivate: [AuthGuard],
		component: LocationsListComponent,
	},
	{
		path: 'location/:id',
		canActivate: [AuthGuard],
		component: LocationComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: `connect-bus-layouts`,
		canActivate: [AuthGuard],
		loadComponent: () =>
			import('./connect-bus-layouts/connect-bus-layouts.component').then((c) => c.ConnectBusLayoutsComponent),
	},
	{
		path: `stop/:kind`,
		canActivate: [AuthGuard],
		loadComponent: () => import('./stops/stops-list/stops-list.component').then((c) => c.StopsListComponent),
	},
	{
		path: 'stop/:kind/grid',
		loadComponent: () => import('./stops/stop-grid/stop-grid.component').then((c) => c.StopGridComponent),
	},
	{
		path: 'stop/:kind/:id',
		canActivate: [AuthGuard],
		component: StopComponent,
	},
	{
		path: 'product',
		canActivate: [AuthGuard],
		component: ProductsListComponent,
	},
	{
		path: 'product/:id',
		canActivate: [AuthGuard],
		component: ProductComponent,
	},
	{
		path: 'www/banner',
		canActivate: [AuthGuard],
		component: WwwBannerListComponent,
	},
	{
		path: 'www/banner/:id',
		canActivate: [AuthGuard],
		component: WwwBannerComponent,
	},
	{
		path: 'groupOffer',
		canActivate: [AuthGuard],
		component: GroupOfferListComponent,
	},
	{
		path: 'groupOffer/:id',
		canActivate: [AuthGuard],
		component: GroupOfferComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'terms',
		canActivate: [AuthGuard],
		component: TermsComponent,
	},
	{
		path: 'optionalTrip/:id',
		canActivate: [AuthGuard],
		component: OptionalTripComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'optionalTrip/add',
		canActivate: [AuthGuard],
		component: OptionalTripComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'optionalTrip',
		canActivate: [AuthGuard],
		component: OptionalTripListComponent,
	},
	{
		path: 'eventBusinessPayments',
		canActivate: [AuthGuard],
		component: EventBusinessPaymentsTableComponent,
	},
	{
		path: 'codebook',
		canActivate: [AuthGuard],
		component: CodebookListComponent,
	},
	{
		path: 'codebook/tag',
		canActivate: [AuthGuard],
		component: TagListComponent,
	},
	{
		path: 'codebook/tag/:id',
		canActivate: [AuthGuard],
		component: TagComponent,
	},
	{
		path: 'codebook/tag/add',
		canActivate: [AuthGuard],
		component: TagComponent,
	},
	{
		path: 'codebook/product_convenience',
		canActivate: [AuthGuard],
		component: ConvenienceListComponent,
	},
	{
		path: 'codebook/product_convenience/:id',
		canActivate: [AuthGuard],
		component: ConvenienceComponent,
	},
	{
		path: 'codebook/product_convenience/add',
		canActivate: [AuthGuard],
		component: ConvenienceComponent,
	},
	{
		path: 'codebook/sell_category',
		canActivate: [AuthGuard],
		component: SellCategoryListComponent,
	},
	{
		path: 'codebook/sell_category/:id',
		canActivate: [AuthGuard],
		component: SellCategoryComponent,
	},
	{
		path: 'codebook/sell_category/add',
		canActivate: [AuthGuard],
		component: SellCategoryComponent,
	},
	{
		path: 'codebook/room_type',
		canActivate: [AuthGuard],
		component: RoomTypeListComponent,
	},
	{
		path: 'codebook/room_type/:id',
		canActivate: [AuthGuard],
		component: RoomTypeComponent,
	},
	{
		path: 'codebook/room_type/add',
		canActivate: [AuthGuard],
		component: RoomTypeComponent,
	},
	{
		path: 'codebook/contacts_destiny',
		canActivate: [AuthGuard],
		component: ContactsDestinyListComponent,
	},
	{
		path: 'codebook/contacts_destiny/:id',
		canActivate: [AuthGuard],
		component: ContactsDestinyComponent,
	},
	{
		path: 'codebook/contacts_destiny/add',
		canActivate: [AuthGuard],
		component: ContactsDestinyComponent,
	},
	{
		path: 'codebook/address_kind',
		canActivate: [AuthGuard],
		component: AddressKindListComponent,
	},
	{
		path: 'codebook/address_kind/:id',
		canActivate: [AuthGuard],
		component: AddressKindComponent,
	},
	{
		path: 'codebook/address_kind/add',
		canActivate: [AuthGuard],
		component: AddressKindComponent,
	},
	{
		path: 'codebook/decription_header',
		canActivate: [AuthGuard],
		component: DecriptionHeaderListComponent,
	},
	{
		path: 'codebook/decription_header/:id',
		canActivate: [AuthGuard],
		component: DecriptionHeaderComponent,
	},
	{
		path: 'codebook/decription_header/add',
		canActivate: [AuthGuard],
		component: DecriptionHeaderComponent,
	},
	{
		path: 'codebook/currency',
		canActivate: [AuthGuard],
		component: CurrencyListComponent,
	},
	{
		path: 'codebook/currency/:id',
		canActivate: [AuthGuard],
		component: CurrencyComponent,
	},
	{
		path: 'codebook/currency/add',
		canActivate: [AuthGuard],
		component: CurrencyComponent,
	},
	{
		path: 'codebook/product_kind/description_requirement/:id',
		canActivate: [AuthGuard],
		component: DescripctionRequirementListComponent,
	},
	{
		path: 'codebook/product_kind/description_requirement/:productKind/:id',
		canActivate: [AuthGuard],
		component: DescriptionRequirementComponent,
	},
	{
		path: 'codebook/product_kind/description_requirement/:id/add',
		canActivate: [AuthGuard],
		component: DescriptionRequirementComponent,
	},
	{
		path: 'codebook/catalogList',
		canActivate: [AuthGuard],
		component: OfersCatalogListComponent,
	},
	{
		path: 'codebook/catalogList/:id',
		canActivate: [AuthGuard],
		component: OffersCatalogComponent,
	},
	{
		path: 'codebook/product_kind',
		canActivate: [AuthGuard],
		component: ProductKindListComponent,
	},
	{
		path: 'codebook/season_list',
		canActivate: [AuthGuard],
		component: SeasonListComponent,
	},
	{
		path: 'codebook/season_list/:id',
		canActivate: [AuthGuard],
		component: CodebookSeasonComponent,
	},
	{
		path: 'codebook/season_list/add',
		canActivate: [AuthGuard],
		component: CodebookSeasonComponent,
	},
	{
		path: 'codebook/insurer',
		canActivate: [AuthGuard],
		loadComponent: () =>
			import('./codebook/insurer/insurer-list/insurer-list.component').then((m) => m.InsurerListComponent),
	},
	{
		path: 'codebook/insurer/add',
		canActivate: [AuthGuard],
		loadComponent: () =>
			import('./codebook/insurer/insurer-form/insurer-form.component').then((m) => m.InsurerFormComponent),
	},
	{
		path: 'codebook/insurer/:id',
		canActivate: [AuthGuard],
		loadComponent: () =>
			import('./codebook/insurer/insurer-form/insurer-form.component').then((m) => m.InsurerFormComponent),
	},
	{
		path: 'codebook/check-list',
		canActivate: [AuthGuard],
		loadComponent: () => import('./codebook/check-list/check-list.component').then((m) => m.CheckListComponent),
	},
	{
		path: 'codebook/:kind',
		canActivate: [AuthGuard],
		component: DefaultCodebookListComponent,
	},
	{
		path: 'codebook/:kind/:id',
		canActivate: [AuthGuard],
		component: CodebookComponent,
	},
	{
		path: 'codebook/:kind/add',
		canActivate: [AuthGuard],
		component: CodebookComponent,
	},
	{
		path: 'organization',
		canActivate: [AuthGuard],
		component: OrganizationListComponent,
	},
	{
		path: 'organization/:id',
		canActivate: [AuthGuard],
		component: OrganizationComponent,
	},
	{
		path: 'www/section',
		canActivate: [AuthGuard],
		component: SectionListComponent,
	},
	{
		path: 'www/section/:id',
		canActivate: [AuthGuard],
		component: SectionComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'www/descriptionTemplate',
		canActivate: [AuthGuard],
		component: WwwDescriptionTemplateListComponent,
	},
	{
		path: 'www/descriptionTemplate/:id',
		canActivate: [AuthGuard],
		component: WwwDescriptionTemplateComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'www/descriptionMain',
		canActivate: [AuthGuard],
		component: WwwMainDescriptionListComponent,
	},
	{
		path: 'www/descriptionMain/:id',
		canActivate: [AuthGuard],
		component: WwwMainDescriptionComponent,
		canDeactivate: [PendingChangesGuard],
	},
	{
		path: 'paymentTermList',
		canActivate: [AuthGuard],
		component: BagPaymentTermListComponent,
	},
	{
		path: 'paymentList',
		canActivate: [AuthGuard],
		component: BagPaymentListComponent,
	},
	{
		path: 'www/footer',
		canActivate: [AuthGuard],
		component: WwwFooterComponent,
	},
	{
		path: 'thread',
		canActivate: [AuthGuard],
		component: MessagesListComponent,
	},
	{
		path: 'thread/:id',
		canActivate: [AuthGuard],
		component: MessageComponent,
	},
	{
		path: 'taskBag',
		canActivate: [AuthGuard],
		component: GeneralTasksListComponent,
	},
	{
		path: 'refresh',
		canActivate: [AuthGuard],
		component: OfferRefreshButtonComponent,
	},
	{
		path: 'payment/mass_rebooking',
		canActivate: [AuthGuard],
		component: ImportMassRebookingComponent,
	},
	{
		path: 'vindication/invoices',
		canActivate: [AuthGuard],
		component: InvoiceComponent,
	},
	{
		path: 'vindication/invoice/mail',
		canActivate: [AuthGuard],
		component: InvoiceMailComponent,
	},
	{
		path: 'vindication/contractors',
		canActivate: [AuthGuard],
		component: ContractorComponent,
	},
	{
		path: 'vindication/contractors/:id',
		canActivate: [AuthGuard],
		component: ContractorEditComponent,
	},
	{
		path: 'vindication/mails-sent',
		canActivate: [AuthGuard],
		loadComponent: () => import('./vindication/mails-sent/mails-sent.component').then((m) => m.MailsSentComponent),
	},
	{
		path: 'analytics',
		canActivate: [AuthGuard],
		loadChildren: () => import('./analytics/analytics.routes').then((m) => m.ANALYTICS_ROUTES),
	},
	{
		path: 'tms',
		canActivate: [AuthGuard],
		loadChildren: () => import('./tms/tms.routes').then((m) => m.TMS_ROUTES),
	},
];

@NgModule({
	declarations: [
		AppComponent,
		ReservationSummaryPaymentsUserdataDialogComponent,
		DynamicTableComponent,
		DynamicTableV2Component,
		QuestionDialogComponent,
		EventsTimelineComponent,
		PriceWithCurrencyComponent,
		LoaderComponent,
		PhoneNumberComponent,
		InfoboxComponent,
		StripHtmlPipe,
		SelectComponent,
		CheckboxesComponent,
		PhpTasksComponent,
		VehiclesComponent,
		VehiclesListComponent,
		DriversComponent,
		DriversCalendarComponent,
		DriversSalaryDaysComponent,
		TouristGuidesComponent,
		TouristGuidesListComponent,
		LeadsComponent,
		LeadsListComponent,
		LeadsShowComponent,
		LeadsShowTasksComponent,
		LeadsShowHistoryComponent,
		LeadsShowTagsComponent,
		DefinitionsComponent,
		TourismCalendarComponent,
		TourismCalendarEventViewComponent,
		TourismEventsComponent,
		TourismEventsListComponent,
		TourismStopsComponent,
		VehiclesFiltersComponent,
		CompanyComponent,
		CompanyListComponent,
		LeadsListFiltersComponent,
		DefinitionsTagsLeadsComponent,
		TourismEventsFiltersComponent,
		LeadsShowDetailComponent,
		LeadsHistoryComponent,
		PhpTasksListComponent,
		PhpTaskComponent,
		OffersScanManageComponent,
		OffersListComponent,
		OffersShowComponent,
		OfersCatalogListComponent,
		SeasonComponent,
		CodebookSeasonComponent,
		OffersParmsComponent,
		SeasonFiltersComponent,
		TourismMapComponent,
		ReservationListRefreshComponent,
		ReservationRefreshComponent,
		UserComponent,
		UsersListComponent,
		TourismMapTransportComponent,
		TourismMapPeopleComponent,
		TourismTimetableComponent,
		TourismTimetableVehiclesComponent,
		PostDocumentTypesComponent,
		TourismTransportListComponent,
		PostSendersComponent,
		TourismSchedulesComponent,
		TourismSchedulesFiltersComponent,
		PostInComponent,
		PostOutComponent,
		PostInListComponent,
		PostOutListComponent,
		TourismBriefingComponent,
		TourismBriefingFiltersComponent,
		TourismEventsDocumentsComponent,
		TourismTransportListConfirmationComponent,
		TimetableListComponent,
		TimetableComponent,
		TimetableHoursComponent,
		TimetableMapComponent,
		TimetableTransportListsComponent,
		TimetableListFiltersComponent,
		DriversCalendarFiltersComponent,
		DriverEventComponent,
		UsersGroupsListComponent,
		UsersGroupComponent,
		TouristGuidesCalendarComponent,
		TouristGuidesEventComponent,
		TouristGuidesFiltersComponent,
		SettingsComponent,
		TourismEventsHistoryComponent,
		HistoryComponent,
		VehiclesHistoryComponent,
		DriversHistoryComponent,
		TouristGuidesHistoryComponent,
		BankAccountsComponent,
		BankAccountComponent,
		BankCardsComponent,
		BankCardComponent,
		BankAccountOwnersComponent,
		BankCardOwnersComponent,
		PhpTasksListFiltersComponent,
		BankTransactionsTypesComponent,
		SimpleComponentComponent,
		BankCardTransactionsComponent,
		BankTransactionsComponent,
		BankTransactionSplitComponent,
		BankTransactionsFiltersComponent,
		BankTransactionsBulkUpdateDialogComponent,
		UsersDepartmentsComponent,
		BankHistoryOperationsComponent,
		BankCardSetComponent,
		BankTransactionsNamesComponent,
		TourismEventsHistoryDriversComponent,
		PhpTaskAddComponent,
		TourismEventsStopComponent,
		TourismEventsFullComponent,
		TourismEventsMalfunctionComponent,
		TourismEventsAntennaBusComponent,
		TourismEventsWashComponent,
		TourismEventsRentComponent,
		TourismEventsEmptyComponent,
		TourismEventsServiceComponent,
		EventBusinessPaymentsComponent,
		EventBusinessIssuesComponent,
		VehiclesDocumentsListComponent,
		VehiclesDocumentsFiltersComponent,
		EventBusinessIssuesTableComponent,
		PostFiltersComponent,
		TransportLeadsComponent,
		TransportLeadsListComponent,
		TransportLeadsListFiltersComponent,
		TagsComponent,
		TouristGuidesRewardComponent,
		TourismEventsDutyComponent,
		TouristGuidesRewardFiltersComponent,
		EventBusinessFiltersComponent,
		TourismEventsLinkComponent,
		DispatcherComponent,
		TransportTemplatesListComponent,
		TransportsMapComponent,
		TransportsListComponent,
		FocustipComponent,
		TransportTemplatesTableComponent,
		TourismEventsFullDriversAddComponent,
		TransportsTableComponent,
		DriversSalaryPaymentsComponent,
		DriversEventAddComponent,
		TransportTemplatesMapComponent,
		AccommodationAvailabilitySetComponent,
		TourismCalendarEventFiltersComponent,
		AccommodationListComponent,
		TransportsMapTemplatesComponent,
		MapComponent,
		MapCityComponent,
		MapVehiclesComponent,
		MapStopsComponent,
		MapTimetableComponent,
		TransportsCopyComponent,
		ResidentBaseTripListComponent,
		ResidentBaseTripComponent,
		ResidentTripComponent,
		ResidentTripListComponent,
		ResidentTripReservationComponent,
		ReservationsPeopleListComponent,
		ResidentDocumentComponent,
		AccommodationListProductsComponent,
		AccommodationListProductComponent,
		AccommodationListProductRoomComponent,
		AccommodationListReservationsComponent,
		AccommodationListAvailabilitiesComponent,
		AccommodationListReservationComponent,
		AccommodationListMonthsComponent,
		PromotionsListComponent,
		PromotionsComponent,
		ServicesListComponent,
		ServicesFormComponent,
		NavigatorImportAnalizComponent,
		SettlementBonusListComponent,
		SettlementBonusComponent,
		AccommodationGenerateComponent,
		ResidentTripReservationListComponent,
		ResidentCustomerComponent,
		ResidentCustomerListComponent,
		ResidentSoldReservationComponent,
		ResidentSoldReservationFiltersComponent,
		ResidentTripListFilterComponent,
		ResidentCustomerListFilterComponent,
		ResidentBaseTripListFilterComponent,
		InsuranceGenerateComponent,
		TourismEventsSeasonComponent,
		AccommodationAvailabilityIstourComponent,
		ResidentSelectRestaurantComponent,
		TestingComponent,
		GoogleContactsListComponent,
		GoogleContactsComponent,
		PromotionsListFiltersComponent,
		WwwMenuListComponent,
		DriversSelectMonthComponent,
		DriversModalInfoComponent,
		DriversSalaryDaysListComponent,
		DriversSalarySummaryMonthComponent,
		DriversRateSetComponent,
		DriversDietSetComponent,
		TourismTransportFiltersComponent,
		DriversSalaryMonthListComponent,
		DriversSalaryMonthListFiltersComponent,
		LocationComponent,
		LocationsListComponent,
		LocationsListFiltersComponent,
		StopComponent,
		OffersCatalogComponent,
		ProductsListComponent,
		ProductsListFiltersComponent,
		WwwBannerComponent,
		WwwBannerListComponent,
		ProductComponent,
		ProductBasicComponent,
		ProductAdditionalComponent,
		ProductMultimediasComponent,
		ProductDescriptionComponent,
		GroupOfferListComponent,
		GroupOfferListFiltersComponent,
		GroupOfferComponent,
		GroupOfferBasicComponent,
		GroupOfferTermsComponent,
		ProductRoomsComponent,
		ProductRoomsAddComponent,
		ProductRoomsEditComponent,
		ProductRoomsSlaveComponent,
		SettlementBonusListFilterComponent,
		TermsComponent,
		TermsFiltersComponent,
		OptionalTripComponent,
		OptionalTripListComponent,
		OptionalTripMultimediasComponent,
		OptionalTripBasicComponent,
		OptionalTripLocationsComponent,
		OptionalTripLocationAddComponent,
		LocationBasicComponent,
		LocationMultimediasComponent,
		LocationDescriptionsComponent,
		LocationDescriptionAddComponent,
		LocationOptionalTripListComponent,
		EventBusinessPaymentsTableComponent,
		EventBusinessPaymentsTableFiltersComponent,
		CodebookListComponent,
		CodebookComponent,
		TagListComponent,
		ConvenienceListComponent,
		SellCategoryListComponent,
		RoomTypeListComponent,
		TagComponent,
		ConvenienceComponent,
		SellCategoryComponent,
		RoomTypeComponent,
		ContactsDestinyListComponent,
		ContactsDestinyComponent,
		AddressKindListComponent,
		AddressKindComponent,
		DefaultCodebookListComponent,
		DecriptionHeaderComponent,
		DecriptionHeaderListComponent,
		CurrencyComponent,
		CurrencyListComponent,
		DescripctionRequirementListComponent,
		ProductKindListComponent,
		DescriptionRequirementComponent,
		SettlementTermListComponent,
		SettlementTermFiltersComponent,
		GroupOfferPriceComponent,
		GroupOfferPriceFiltersComponent,
		OrganizationListComponent,
		OrganizationFiltersComponent,
		OrganizationComponent,
		OrganizationPrimaryComponent,
		OrganizationDataDownloadComponent,
		OrganizationAddressAddComponent,
		OrganizationBankAccountsComponent,
		OrganizationWorkersAddComponent,
		OrganizationWorkersComponent,
		OrganizationAddressEditComponent,
		OrganizationWorkersEditComponent,
		SectionListComponent,
		SectionComponent,
		SectionFilterComponent,
		DriversDocumentsListComponent,
		DriversDocumentsListFiltersComponent,
		GroupOfferPriceSettingComponent,
		GroupOfferPriceSettingDialogComponent,
		GroupOfferPriceSettingPublicationDialogComponent,
		GroupOfferPriceHistoryDialogComponent,
		GroupOfferActivePriceSettingDialogComponent,
		VehicleDeviceCmaComponent,
		SeasonBagNewComponent,
		SeasonBagNewFiltersComponent,
		SeasonBagNewModalComponent,
		MapDialogComponent,
		WwwDescriptionTemplateComponent,
		WwwDescriptionTemplateListComponent,
		WwwMainDescriptionComponent,
		WwwMainDescriptionListComponent,
		ServiceBasicComponent,
		ServiceAdditionalComponent,
		ServiceMultimediaComponent,
		ServiceDialogComponent,
		ReservationComponent,
		ReservationSummaryPaymentsDialogComponent,
		ReservationSummaryComponent,
		ReservationSummaryOwnerComponent,
		ReservationSummaryOwnerDialogComponent,
		ReservationSummaryAgentComponent,
		ReservationSummaryAgentDialogComponent,
		ReservationSummaryAlertsComponent,
		ReservationSummaryStatusComponent,
		ReservationSummaryGuideComponent,
		ReservationSummaryCombineComponent,
		ReservationSummaryAccommodationComponent,
		ReservationSummaryAccomodationOfferRoomsComponent,
		ReservationSummaryAccomodationOfferRoomsDialogComponent,
		ReservationAccommodationOfferParticipantComponent,
		ReservationAccomodationOfferParticipantDialogComponent,
		ReservationAccomodationServiceComponent,
		ReservationSummaryAccomodationServiceDialogComponent,
		ReservationSummaryPaymentsComponent,
		ReservationSummaryDocumentsComponent,
		ReservationSummaryDialogHandlerComponent,
		BagReservationListTableComponent,
		BagReservationListFilterComponent,
		BagReservationListStatisticComponent,
		BagReservationListComponent,
		SeasonBagNewModalStatusChangeComponent,
		DriversSalaryConfirmHistoryComponent,
		SettlementBonusHistoryDetailComponent,
		BankCardsFiltersComponent,
		AccommodationsComponent,
		AccommodationsDatebarComponent,
		AccommodationsProductsSelectComponent,
		AccommodationsDestinationSelectComponent,
		AccommodationsProductsComponent,
		AccommodationsProductsRoomsComponent,
		AccommodationsProductsRoomsAvailabilitiesComponent,
		BagTransportTermComponent,
		BagTransportTermModalTransportComponent,
		BagTransportFiltersComponent,
		ReservationSummaryOfferComponent,
		BagPaymentTermListComponent,
		BagPaymentTermListTableComponent,
		BagPaymentTermListFiltersComponent,
		BagPaymentTermListTableSummaryComponent,
		BagPaymentListComponent,
		BagPaymentListTableComponent,
		BagPaymentListTableFiltersComponent,
		BagPaymentListTableSummaryComponent,
		BagPaymentListAddComponent,
		BagPaymentListAddModalComponent,
		BagPaymentListAddPositionModalComponent,
		NoteDialogComponent,
		TasksDialogComponent,
		ReservationTaskAndNotesComponent,
		ConfirmTaskDialogComponent,
		ReservationSummaryCancelDialogComponent,
		ReservationSummaryPaymentsSummaryComponent,
		ReservationSummaryAccomodationOfferRoomsDialogOfferChangeDialogComponent,
		AccommodationsReservationComponent,
		AccommodationsProductWithoutRoomsComponent,
		ReservationMessageListComponent,
		BagPaymentListProfileAddComponent,
		AccommodationsReservationsDokComponent,
		ReservationDocumentsComponent,
		ReservationSummaryGuideDialogComponent,
		ReservationMessageDialogComponent,
		ReservationMessageDetailsDialogComponent,
		ReservationMessageSendComponent,
		OrganizationDialogAddComponent,
		ReservationSummaryAcommodationOfferParticipantChangeDialogComponent,
		WwwFooterComponent,
		ReservationMessageGalleryComponent,
		DynamicTableV2ColumnOptionDialogComponent,
		TourismEventsOrganizationDialogComponent,
		ServicesListFiltersComponent,
		ReservationSummaryInvoiceComponent,
		ReservationSummaryInvoiceDialogComponent,
		MessagesListComponent,
		MessageComponent,
		GeneralTasksListComponent,
		TourismEventsUserAddComponent,
		MessageFiltersComponent,
		GeneralTasksFilterComponent,
		ConfirmChangeDialogComponent,
		MessageTabsComponent,
		MessageTableComponent,
		MessageAssignDialogComponent,
		OfferRefreshButtonComponent,
		SeasonListComponent,
		OrganizationBankAccountsAddEditComponent,
		BytesConversionPipe,
		CurrencyCodePipe,
		DateBoundsPipe,
		DecodeBase64Pipe,
		PercentValuePipe,
		YesNoPipe,
		NumberToDecimalStrPipe,
		TransportKindIconPipe,
		OrganizationContactAddEditDialogComponent,
		ComfortPackageDepartureComponent,
		ComfortPackageReturnComponent,
		ComfortPackageOneTransportComponent,
	],
	bootstrap: [AppComponent],
	exports: [RouterModule],
	imports: [
		RouterModule.forRoot(routes, {}),
		FormatDateTimePipe,
		FormsModule,
		NgSelectModule,
		CKEditorModule,
		BrowserAnimationsModule,
		MatSlideToggleModule,
		MatTableModule,
		MatPaginatorModule,
		MatSortModule,
		MatTooltipModule,
		MatAutocompleteModule,
		MatFormFieldModule,
		MatCheckboxModule,
		DragDropModule,
		MatButtonModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatSelectModule,
		MatInputModule,
		MatRadioModule,
		MatDialogModule,
		MatTabsModule,
		MatCardModule,
		MatChipsModule,
		MatSnackBarModule,
		MatIconModule,
		MatDividerModule,
		ReactiveFormsModule,
		NgxMatTimepickerModule,
		MatMenuModule,
		MatExpansionModule,
		MatProgressBarModule,
		MatProgressSpinnerModule,
		AngularEditorModule,
		NgOptimizedImage,
		AuthModule,
		NavbarModule,
		ReservationSearchModule,
		EditorComponent,
		ReservationSummaryCommissionEditDialogComponent,
		ReservationSummaryUserDocumentsComponent,
		PricePipe,
		PriceCurrencyPipe,
		PaymentTermStatusLabelPipe,
		PaymentStatusLabelPipe,
		StatusLabelPipe,
		FastFiltersComponent,
		GenericMatAutocompleteComponent,
		SelectMultiComponent,
		AccommodationsAvailabilitySetComponent,
		StarsComponent,
		TourismEventsFleetComponent,
		TourismEventsOrganizationComponent,
		ComfortPackageComponent,
		TourismEventsTechnicalEmptyComponent,
		ClipboardModule,
		ConvertCommentPipe,
		TourismEventsVehicleAddComponent,
		UtcDatetimeDirective,
		OwnerSearchComponent,
		OrganizationInsurerComponent,
		TranslateModule.forRoot(),
		TranslatePipe,
		NoteListComponent,
		TourismEventTransportTermSlotsComponent,
		BankAccountFormatPipe,
		BagTransportTermTableComponent,
		TasksListModule,
		ReservationSmsListComponent,
		ProductOrganizationConfigComponent,
		MatToolbarModule,
		MatSidenavModule,
		MatListModule,
		ThemeChipSplitComponent,
		ProductCheckListComponent,
		BagHistoryComponent,
		TouristGuidesRatesComponent,
		TouristGuidesIntegrationComponent,
		TouristGuidesSalaryListComponent,
		DriverLedgerListComponent,
		DriversIntegrationComponent,
		DriversSalaryV2GratyfikantComponent,
		DriversSalaryV2Component,
		DriversLedgerComponent,
		ProductCheckListComponent,
		ProductCostsComponent,
	],
	providers: [
		{
			provide: HTTP_INTERCEPTORS,
			useClass: RequestTrackerInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: TokenInterceptor,
			multi: true,
		},
		RequestTrackerInterceptor,
		DocumentDownloader,
		PendingChangesGuard,
		{
			provide: API_URL_TOKEN,
			useValue: `${environment.apiUrl}/`,
		},
		DatePipe,
		BytesConversionPipe,
		CountValueTransportTerm,
		CurrencyCodePipe,
		DateBoundsPipe,
		PricePipe,
		PriceCurrencyPipe,
		DecodeBase64Pipe,
		FormatDateTimePipe,
		PercentValuePipe,
		YesNoPipe,
		NumberToDecimalStrPipe,
		TransportKindIconPipe,
		provideHttpClient(withInterceptorsFromDi(), withInterceptors([httpErrorsInterceptor])),
		{
			provide: APP_INITIALIZER,
			useFactory: (initService: AppInitService) => () => initService.initialize(),
			deps: [AppInitService],
			multi: true,
		},
	],
})
export class AppModule {}
